import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { useAppVersion } from '../lib/version.js';
import { Alert, Field } from '../components/ui.jsx';

const STEPS = [
  ['Delivery order', 'The counter enters the goods, the buyer and the godown — once.'],
  ['Godown despatch', 'The godown confirms what actually went out, batch by batch, first-expiry-first-out.'],
  ['Tax invoice', 'Built from the despatch, with each batch’s chemical properties, and posted to Tally.'],
];

export default function Login({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('signin');
  const [sent, setSent] = useState(null);
  const version = useAppVersion();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.login(email, password);
      onSignedIn(await api.get('/api/auth/me'));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/auth/forgot-password', { email });
      setSent(res);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="auth">
      <aside className="auth-art">
        <div className="inline">
          <div className="rail-mark">IV</div>
          <b style={{ letterSpacing: '-.2px' }}>InvIT</b>
        </div>
        <div>
          <h2>Three documents. One keying of the goods.</h2>
          <div className="flow">
            {STEPS.map(([title, body], i) => (
              <div className="step" key={title}>
                <div className="n">{i + 1}</div>
                <p><b>{title}</b>{body}</p>
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: '#8f98a4' }}>Inventory &amp; billing for godown despatch · synced with TallyPrime</div>
      </aside>

      <div className="auth-form">
        {mode === 'signin' ? (
          <form className="inner" onSubmit={submit}>
            <h1>Sign in</h1>
            <p className="page-sub" style={{ marginBottom: 22 }}>Use the account your administrator set up for this workspace.</p>
            <Alert>{error}</Alert>
            <Field label="Email">
              <input type="email" value={email} autoComplete="username" autoFocus
                onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Password">
              <input type="password" value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            <button className="btn big" style={{ width: '100%', justifyContent: 'center' }} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <button type="button" className="btn ghost" style={{ width: '100%', marginTop: 10 }}
              onClick={() => { setMode('forgot'); setError(null); }}>
              Forgotten your password?
            </button>
          </form>
        ) : (
          <form className="inner" onSubmit={forgot}>
            <h1>Reset your password</h1>
            <p className="page-sub" style={{ marginBottom: 22 }}>
              We will send a one-time link to the address on your account.
            </p>
            <Alert>{error}</Alert>
            {sent ? (
              <>
                <Alert kind="ok">{sent.message}</Alert>
                {sent.dev_token && (
                  <Alert kind="info">
                    No mail channel is configured on this deployment, so here is the link:
                    <div className="mono" style={{ marginTop: 6, wordBreak: 'break-all' }}>
                      /reset-password?token={sent.dev_token}
                    </div>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Field label="Email">
                  <input type="email" value={email} autoComplete="username" autoFocus
                    onChange={(e) => setEmail(e.target.value)} required />
                </Field>
                <button className="btn big" style={{ width: '100%', justifyContent: 'center' }} disabled={busy}>
                  {busy ? 'Sending…' : 'Send the link'}
                </button>
              </>
            )}
            <button type="button" className="btn ghost" style={{ width: '100%', marginTop: 10 }}
              onClick={() => { setMode('signin'); setSent(null); setError(null); }}>
              Back to sign in
            </button>
          </form>
        )}
        {version && <div className="auth-version">InvIT v{version}</div>}
      </div>
    </div>
  );
}
